import React from "react";
import { m } from "motion/react";
import useUserStore, { useIndexStore } from "@/state/user/useUserStore";
import { TabText } from "@/components/styles";
import { Folder, Type } from "lucide-react";
import { ScrollView } from "react95";
import { TextInputComponent } from "@/components/ui/text-input-component";
import Image from "@/components/ui/image";
import styled from "styled-components";
import ProjectComponent from "@/components/ui/project-component";

const StyledMotionDiv = styled(m.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  padding: 24px;
  box-sizing: border-box;

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const AssetComponent = ({
  parentVariants,
  childVariants,
  color,
  assets,
  noProjectFolder,
  noTabs,
}: {
  parentVariants: any;
  childVariants: any;
  color: string;
  assets: {
    tab: "head" | "clothing" | "face" | "eye" | "misc";
    items: string[];
    icon: React.ReactNode;
  }[];
  noProjectFolder?: boolean;
  noTabs?: boolean;
}) => {
  const [
    tabs,
    setTabs,
    setAssets,
    textFields,
    addTextField,
    restrictedFonts,
    uploadAssets,
    ratio,
    selectedAssets,
  ] = useUserStore((state) => [
    state.tabs,
    state.setTabs,
    state.setAssets,
    state.textFields,
    state.addTextField,
    state.restrictedFonts,
    state.uploadAssets,
    state.ratio,
    state.assets,
  ]);

  const [
    lastAssetIndex,
    setLastAssetIndex,
    lastTextFieldIndex,
    setLastTextFieldIndex,
  ] = useIndexStore((state) => [
    state.lastAssetIndex,
    state.setLastAssetIndex,
    state.lastTextFieldIndex,
    state.setLastTextFieldIndex,
  ]);

  const allFieldsLength =
    textFields.length + selectedAssets.length + uploadAssets.length;

  const setAssetsHandler = (img: string) => {
    const id = crypto.randomUUID();
    setAssets((currentAssets) => [
      ...currentAssets,
      {
        id: id,
        img: img,
        flipHorizontal: false,
        flipVertical: false,
        zIndex: allFieldsLength + 1,
        index: lastAssetIndex + 1,
      },
    ]);
    setLastAssetIndex(lastAssetIndex + 1);
  };

  // Calculate tab width and position based on noProjectFolder prop
  const getTabWidth = () => {
    if (noProjectFolder) {
      return "20%"; // 5 tabs, each taking 20%
    }
    return "16.66%"; // 6 tabs, each taking 16.66
  };

  const getTabPosition = () => {
    if (noProjectFolder) {
      // When no project folder, map the 5 asset tabs to 0-80 positions
      switch (tabs) {
        case "head":
          return "0%";
        case "clothing":
          return "20%";
        case "face":
          return "40%";
        case "eye":
          return "60%";
        case "misc":
          return "80%";
        default:
          return "0%";
      }
    } else {
      // Original logic with project folder
      switch (tabs) {
        case "project":
          return "0%";
        case "head":
          return "16.66%";
        case "clothing":
          return "33.32%";
        case "face":
          return "49.98%";
        case "eye":
          return "66.64%";
        case "misc":
          return "83.3%";
        default:
          return "0%";
      }
    }
  };

  // Set default tab when noProjectFolder is true and current tab is "project"
  React.useEffect(() => {
    if (noProjectFolder && tabs === "project") {
      setTabs("head");
    }
  }, [noProjectFolder, tabs, setTabs]);

  // Get all assets when noTabs is true
  const getAllAssets = () => {
    return assets.flatMap(asset => asset.items);
  };

  return (
    <>
      <m.div
        variants={parentVariants}
        initial="initial"
        animate="animate"
        style={{
          background: "#cccccc",
          boxSizing: "border-box",
          borderRadius: "0.25rem",
          border: "4px solid #000000",
          boxShadow: "2px 2px 0px 0px #000000",
          height: "calc(100% - 60px)",
          maxHeight: "48rem",
        }}
      >
        <m.div
          style={{
            background: color,
            boxSizing: "border-box",
            padding: "8px 8px 8px 8px",
            borderBottom: "4px solid #000000",
            minHeight: "68px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {!noTabs && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <div
                style={{
                  background: "#00000075",
                  width: getTabWidth(),
                  borderRadius: "0.25rem",
                  height: "3rem",
                  position: "absolute",
                  zIndex: 0,
                  left: getTabPosition(),
                  transition: "left 0.25s ease",
                }}
              ></div>
              {!noProjectFolder && (
                <TabText
                  onClick={() => setTabs("project")}
                  selected={tabs === "project"}
                >
                  <Folder />
                </TabText>
              )}
              {Object.values(assets).map((asset) => (
                <TabText
                  key={asset.tab}
                  onClick={() => setTabs(asset.tab)}
                  selected={tabs === asset.tab}
                  noProject={noProjectFolder}
                >
                  {asset.icon}
                </TabText>
              ))}
            </div>
          )}
        </m.div>
        
        {noTabs ? (
          // Show all assets in a single view when noTabs is true
          <ScrollView
            style={{
              height: `calc(100% - 68px)`,
              margin: "0",
              padding: "0",
              overflow: "hidden",
            }}
          >
            <StyledMotionDiv>
              {getAllAssets().map((asset, index) => (
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "1/1",
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={() => setAssetsHandler(asset)}
                  key={index}
                >
                  <Image
                    draggable={false}
                    src={asset}
                    style={{
                      objectFit: "contain",
                    }}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 10vw, (max-width: 1200px) 20vw, 25vw"
                  />
                </div>
              ))}
            </StyledMotionDiv>
          </ScrollView>
        ) : (
          // Original tab-based logic
          (() => {
            switch (tabs) {
              case "head":
                return (
                  <ScrollView
                    style={{
                      height: `calc(100% - 68px)`,
                      margin: "0",
                      padding: "0",
                      overflow: "hidden",
                    }}
                  >
                    <StyledMotionDiv>
                      {assets[0].items.map((asset, index) => (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1/1",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => setAssetsHandler(asset)}
                          key={index}
                        >
                          <Image
                            draggable={false}
                            src={asset}
                            style={{
                              objectFit: "contain",
                            }}
                            alt=""
                            fill
                            sizes="(max-width: 768px) 10vw, (max-width: 1200px) 20vw, 25vw"
                          />
                        </div>
                      ))}
                    </StyledMotionDiv>
                  </ScrollView>
                );
              case "clothing":
                return (
                  <ScrollView
                    style={{
                      height: `calc(100% - 68px)`,
                      margin: "0",
                      padding: "0",
                    }}
                  >
                    <StyledMotionDiv>
                      {assets[1].items.map((asset, index) => (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1/1",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => setAssetsHandler(asset)}
                          key={index}
                        >
                          <Image
                            draggable={false}
                            src={asset}
                            style={{
                              objectFit: "contain",
                            }}
                            alt=""
                            fill
                            sizes="(max-width: 768px) 10vw, (max-width: 1200px) 20vw, 25vw"
                          />
                        </div>
                      ))}
                    </StyledMotionDiv>
                  </ScrollView>
                );
              case "face":
                return (
                  <ScrollView
                    style={{
                      height: `calc(100% - 68px)`,
                      margin: "0",
                      padding: "0",
                    }}
                  >
                    <StyledMotionDiv>
                      {assets[2].items.map((asset, index) => (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1/1",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => setAssetsHandler(asset)}
                          key={index}
                        >
                          <Image
                            draggable={false}
                            src={asset}
                            style={{
                              objectFit: "contain",
                            }}
                            alt=""
                            fill
                            sizes="(max-width: 768px) 10vw, (max-width: 1200px) 20vw, 25vw"
                          />
                        </div>
                      ))}
                    </StyledMotionDiv>
                  </ScrollView>
                );
              case "eye":
                return (
                  <ScrollView
                    style={{
                      height: `calc(100% - 68px)`,
                      margin: "0",
                      padding: "0",
                    }}
                  >
                    <StyledMotionDiv>
                      {assets[3].items.map((asset, index) => (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1/1",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => setAssetsHandler(asset)}
                          key={index}
                        >
                          <Image
                            draggable={false}
                            src={asset}
                            style={{
                              objectFit: "contain",
                            }}
                            alt=""
                            fill
                            sizes="(max-width: 768px) 10vw, (max-width: 1200px) 20vw, 25vw"
                          />
                        </div>
                      ))}
                    </StyledMotionDiv>
                  </ScrollView>
                );
              case "misc":
                return (
                  <ScrollView
                    style={{
                      height: `calc(100% - 68px)`,
                      margin: "0",
                      padding: "0",
                    }}
                  >
                    <StyledMotionDiv>
                      {assets[4].items.map((asset, index) => (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1/1",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => setAssetsHandler(asset)}
                          key={index}
                        >
                          <Image
                            draggable={false}
                            src={asset}
                            style={{
                              objectFit: "contain",
                            }}
                            alt=""
                            fill
                          />
                        </div>
                      ))}
                    </StyledMotionDiv>
                  </ScrollView>
                );
              case "project":
                return noProjectFolder ? null : <ProjectComponent />;
              default:
                break;
            }
          })()
        )}
      </m.div>
    </>
  );
};

export default AssetComponent;
